import { FC, useState, ReactNode, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import {
  X,
  Bank,
  IdentificationCard,
  Wallet,
  Info,
  CheckCircle,
  EnvelopeSimple,
  XCircle,
  HourglassMedium,
  Icon,
  SealCheck,
  Warning,
  Spinner,
} from "@phosphor-icons/react";
import useCheckEligibility from "../hooks/useCheckEligibility";
import { useInitiateMonoLink } from "../hooks/useInitiateMonoLink";
import { useAuth } from "../context/AuthContext";
import { CreditLimitEligibilityStatus } from "../hooks/useCreditLimitEligibilityStatus";
import useLoanPreApprovalRequirements from "../hooks/useLoanPreApprovalRequirements";
import { useStartSetupTask } from "./CompleteSetupTaskList";

type ModalStatus =
  | "idle"
  | "submitted"
  | "failed"
  | "no_bank_account"
  | "already_pending"
  | "result_approved"
  | "result_declined"
  | "no_kyc"
  | "no_alert_account";

interface StatusAction {
  label: string;
  onClick: () => void;
  variant: "primary" | "secondary";
  disabled?: boolean;
}

interface StatusConfig {
  icon: Icon;
  iconBgColor: string;
  iconColor: string;
  title: string;
  description: ReactNode;
  hint?: ReactNode;
  actions: StatusAction[];
}

const StatusView: FC<{ config: StatusConfig }> = ({ config }) => {
  const IconComponent = config.icon;

  return (
    <>
      <div className="flex flex-col items-center text-center py-6">
        <div className={`p-3 ${config.iconBgColor} rounded-full mb-4`}>
          <IconComponent size={48} weight="fill" className={config.iconColor} />
        </div>
        <h2 className="text-xl font-semibold text-deepBlue mb-2">
          {config.title}
        </h2>
        <p className="text-gray-500 text-sm max-w-sm">{config.description}</p>

        {config.hint && (
          <div className="flex items-center gap-2 mt-6 text-gray-600 text-sm">
            {config.hint}
          </div>
        )}
      </div>

      <div
        className={`flex gap-3 mt-4 ${
          config.actions.length === 1 ? "" : "flex-col sm:flex-row"
        }`}
      >
        {config.actions.map((action, index) => (
          <button
            key={index}
            onClick={action.onClick}
            disabled={action.disabled}
            className={`flex-1 rounded-lg py-3 font-medium transition ${
              action.variant === "primary"
                ? "bg-visaro-orange hover:bg-orange-500 text-white disabled:opacity-50"
                : "border border-gray-300 text-gray-700 hover:bg-gray-50"
            }`}
          >
            {action.label}
          </button>
        ))}
      </div>
    </>
  );
};

const CreditLimitEligibilityModal: FC<{
  isOpen: boolean;
  handleClose: () => void;
  eligibilityStatus?: CreditLimitEligibilityStatus;
  hasAlertAccount: boolean;
  kycReady: boolean;
  onRequestAlertAccount: () => Promise<void>;
}> = ({
  isOpen,
  handleClose,
  eligibilityStatus,
  hasAlertAccount,
  kycReady,
  onRequestAlertAccount,
}) => {
  const [status, setStatus] = useState<ModalStatus>("idle");
  const [activatingAccount, setActivatingAccount] = useState(false);
  const [activationError, setActivationError] = useState("");
  const navigate = useNavigate();
  const { user } = useAuth();
  const { missing } = useLoanPreApprovalRequirements();
  const startSetupTask = useStartSetupTask();
  const { initiateMonoLink, initiatingMonoLink } = useInitiateMonoLink();

  useEffect(() => {
    if (!isOpen) return;

    if (eligibilityStatus?.status === "approved") {
      setStatus("result_approved");
    } else if (eligibilityStatus?.status === "declined") {
      setStatus("result_declined");
    } else if (!kycReady) {
      setStatus("no_kyc");
    } else if (!hasAlertAccount) {
      setStatus("no_alert_account");
    } else if (eligibilityStatus?.status === "pending") {
      setStatus("already_pending");
    } else {
      setStatus("idle");
    }
  }, [isOpen, hasAlertAccount, kycReady, eligibilityStatus?.status]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  const { checkEligibility, checkingEligibility } = useCheckEligibility({
    onSuccess: () => {
      setStatus("submitted");
    },
    onError: (error) => {
      if (error.message.toLowerCase().includes("link a bank account")) {
        setStatus("no_bank_account");
      } else if (error.message.toLowerCase().includes("pending")) {
        setStatus("already_pending");
      } else {
        setStatus("failed");
      }
    },
  });

  const handleLinkBankAccount = () => {
    if (user) {
      initiateMonoLink(
        {
          name: `${user.firstname} ${user.lastname}`,
          email: user.email || "",
          ref: user.id,
          redirect_url: window.location.href,
        },
        {
          onSuccess: (data) => {
            if (data.data?.mono_url) {
              window.location.href = data.data.mono_url;
            }
          },
        }
      );
    }
  };

  const handleModalClose = () => {
    setStatus("idle");
    setActivatingAccount(false);
    setActivationError("");
    handleClose();
  };

  const handleLearnMore = () => {
    handleModalClose();
    navigate("/credit-limit-info");
  };

  const handleActivateAndContinue = async () => {
    if (activatingAccount) return;
    setActivatingAccount(true);
    setActivationError("");

    try {
      await onRequestAlertAccount();
      setStatus("idle");
    } catch (error) {
      setActivationError(
        error instanceof Error
          ? error.message
          : "We couldn't set up your loan account. Please try again."
      );
    } finally {
      setActivatingAccount(false);
    }
  };

  const emailHint = user?.email ? (
    <>
      <EnvelopeSimple size={20} weight="bold" />
      <span>Check your inbox for updates</span>
    </>
  ) : (
    <>
      <Info size={20} weight="bold" />
      <span>Check your dashboard for updates</span>
    </>
  );

  const statusConfigs: Record<Exclude<ModalStatus, "idle">, StatusConfig> = {
    no_kyc: {
      icon: IdentificationCard,
      iconBgColor: "bg-amber-100",
      iconColor: "text-amber-500",
      title: "Complete Your Profile First",
      description:
        `Complete the remaining loan setup steps: ${missing.map((task) => task.label.toLowerCase()).join(", ")}.`,
      actions: [
        {
          label: missing[0]?.label ?? "Complete Setup",
          onClick: () => {
            handleModalClose();
            if (missing[0]) startSetupTask(missing[0], { returnTo: "/home" });
          },
          variant: "primary",
        },
        {
          label: "Maybe Later",
          onClick: handleModalClose,
          variant: "secondary",
        },
      ],
    },
    no_alert_account: {
      icon: Wallet,
      iconBgColor: "bg-blue-100",
      iconColor: "text-deepBlue",
      title: "Set up your loan account",
      description: activationError ||
        "Your profile is complete. Set up your linked loan account before checking your credit limit eligibility. This is separate from your Visaro wallet.",
      actions: [
        {
          label: activatingAccount ? "Activating…" : "Activate & Continue",
          onClick: handleActivateAndContinue,
          variant: "primary",
          disabled: activatingAccount,
        },
        {
          label: "Cancel",
          onClick: handleModalClose,
          variant: "secondary",
        },
      ],
    },
    submitted: {
      icon: CheckCircle,
      iconBgColor: "bg-green-100",
      iconColor: "text-green-500",
      title: "Request Submitted",
      description: (
        <>
          Your eligibility check request has been submitted for review.{" "}
          {user?.email
            ? "We'll notify you via email once the review is complete."
            : "Please check back on the platform for updates on your eligibility status."}
        </>
      ),
      hint: emailHint,
      actions: [
        { label: "Done", onClick: handleModalClose, variant: "primary" },
      ],
    },
    already_pending: {
      icon: HourglassMedium,
      iconBgColor: "bg-blue-100",
      iconColor: "text-blue-500",
      title: "Request Already Pending",
      description: (
        <>
          You already have an eligibility check request under review.{" "}
          {user?.email
            ? "We'll notify you via email once the review is complete."
            : "Please check back on the platform for updates."}
        </>
      ),
      hint: emailHint,
      actions: [
        { label: "Got It", onClick: handleModalClose, variant: "primary" },
      ],
    },
    no_bank_account: {
      icon: Bank,
      iconBgColor: "bg-amber-100",
      iconColor: "text-amber-500",
      title: "Link a Bank Account",
      description:
        "To check your eligibility, we need access to your bank statements. Please link at least one bank account to continue.",
      actions: [
        {
          label: initiatingMonoLink ? "Connecting..." : "Link Bank Account",
          onClick: handleLinkBankAccount,
          variant: "primary",
          disabled: initiatingMonoLink,
        },
        {
          label: "Maybe Later",
          onClick: handleModalClose,
          variant: "secondary",
        },
      ],
    },
    failed: {
      icon: XCircle,
      iconBgColor: "bg-red-100",
      iconColor: "text-red-500",
      title: "Request Failed",
      description:
        "We couldn't process your eligibility check request. Please try again or contact support if the problem persists.",
      actions: [
        {
          label: "Try Again",
          onClick: () => setStatus("idle"),
          variant: "primary",
        },
        { label: "Close", onClick: handleModalClose, variant: "secondary" },
      ],
    },
    result_approved: {
      icon: SealCheck,
      iconBgColor: "bg-green-100",
      iconColor: "text-green-500",
      title: "Credit Limit Available",
      description: (
        <>
          Congratulations! You've been approved for a credit limit of{" "}
          <strong>
            ₦{eligibilityStatus?.credit_limit?.toLocaleString() ?? 0}
          </strong>
          . Claiming this amount submits a loan request for partner approval. Funds are not available until that loan is approved and disbursed.
        </>
      ),
      actions: [
        { label: "Got It", onClick: handleModalClose, variant: "primary" },
      ],
    },
    result_declined: {
      icon: Warning,
      iconBgColor: "bg-amber-100",
      iconColor: "text-amber-500",
      title: "Not Eligible Yet",
      description:
        "Your previous request was declined. You can request another review or contact support for more information. A new review does not guarantee approval.",
      actions: [
        { label: "Request another review", onClick: () => setStatus(!kycReady ? "no_kyc" : !hasAlertAccount ? "no_alert_account" : "idle"), variant: "primary", disabled: checkingEligibility },
        { label: "Close", onClick: handleModalClose, variant: "secondary" },
      ],
    },
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 z-[9999] bg-black/30 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            transition={{ type: "spring", stiffness: 200, damping: 20 }}
            className="bg-white rounded-2xl shadow-lg max-w-xl w-full relative my-auto max-h-[85vh] flex flex-col overflow-hidden"
          >
            <button
              onClick={handleModalClose}
              className="absolute top-4 right-4 z-10 p-2 rounded-full bg-white hover:bg-gray-100 transition"
            >
              <X size={20} weight="bold" />
            </button>

            {status !== "idle" ? (
              <div className="p-6 overflow-y-auto">
                <StatusView config={statusConfigs[status]} />
              </div>
            ) : (
              <>
                {/* Fixed header (stays put so the close button is always reachable) */}
                <div className="flex flex-col gap-2 text-center px-6 pt-6 pb-3 shrink-0">
                  <h2 className="text-xl font-semibold text-deepBlue">
                    Check your credit limit eligibility
                  </h2>
                  <p className="text-gray-500 text-sm">
                    Find out the credit limit Visaro may be able to offer you
                    before you need it.
                  </p>
                </div>

                {/* Scrollable body */}
                <div className="px-6 py-3 space-y-4 text-gray-600 text-sm overflow-y-auto flex-1">
                  <p>
                    Visaro runs a one-time eligibility check using your verified
                    information to assess a personalized <strong>credit limit</strong>.
                  </p>

                  <p>
                    If a limit is assigned, you can claim it to submit a loan request.
                    The partner must approve and disburse that loan before funds become available.
                  </p>

                  <p className="font-medium">This step is optional.</p>

                  <div>
                    <div className="font-medium mb-2 text-deepBlue">
                      What we'll check
                    </div>
                    <ul className="space-y-3">
                      <li className="flex items-start gap-3">
                        <IdentificationCard
                          size={20}
                          weight="bold"
                          className="text-visaro-orange mt-1"
                        />
                        <span>NIN &amp; BVN</span>
                      </li>

                      <li className="flex items-start gap-3">
                        <Bank
                          size={20}
                          weight="bold"
                          className="text-visaro-orange mt-1"
                        />
                        <span>Linked bank accounts and statements</span>
                      </li>

                      <li className="flex items-start gap-3">
                        <Wallet
                          size={20}
                          weight="bold"
                          className="text-visaro-orange mt-1"
                        />
                        <span>Linked cards and transaction history</span>
                      </li>

                      <li className="flex items-start gap-3">
                        <Info
                          size={20}
                          weight="bold"
                          className="text-visaro-orange mt-1"
                        />
                        <span>Employment details</span>
                      </li>
                    </ul>
                  </div>

                  <div className="mt-4 space-y-3">
                    <div className="font-medium text-deepBlue">
                      Before you continue
                    </div>

                    <p>
                      <strong>Approval is not guaranteed.</strong> You may be
                      approved, declined, or approved for a lower limit.
                    </p>

                    <p>
                      An assigned credit limit is not a wallet balance. Claiming it
                      starts a separate loan request that still requires partner approval.
                    </p>
                  </div>
                </div>

                {/* Fixed footer: CTAs + consent — stays visible while the body scrolls */}
                <div className="px-6 pt-4 pb-6 shrink-0 border-t border-gray-100 bg-white">
                  <div className="flex flex-col sm:flex-row gap-3">
                    <button
                      onClick={() => checkEligibility()}
                      disabled={checkingEligibility}
                      className="flex-1 bg-visaro-orange hover:bg-orange-500 text-white rounded-lg py-3 font-medium transition disabled:opacity-50 flex items-center justify-center gap-2"
                    >
                      {checkingEligibility && (
                        <Spinner size={16} className="animate-spin" />
                      )}
                      {checkingEligibility ? "Checking..." : "Check Eligibility"}
                    </button>

                    <button
                      onClick={handleLearnMore}
                      className="flex-1 border border-gray-300 rounded-lg py-3 font-medium text-gray-700 hover:bg-gray-50 transition"
                    >
                      Learn More
                    </button>
                  </div>

                  <div className="text-gray-500 text-xs text-center mt-3">
                    By continuing, you consent to Visaro running a one-time
                    eligibility and risk assessment using your verified financial
                    information.
                  </div>
                </div>
              </>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default CreditLimitEligibilityModal;
